import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { StoryReply, storyReplies } from "@/services/firebase/artistWorkspace";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const fmt = (ms: number | null) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");

/**
 * B-19: 限動回覆. Fans reply to a story privately (storyReply); the artist's team reads them here
 * (artistHub storyReplies; artist / editor / staff — reviewers get 403). Read-only in v1; replies are kept 30 days.
 */
export default function ArtistStoryRepliesPanel({ artistId }: { artistId: string }) {
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const cursor = pages[pages.length - 1];
  const list = useQuery({
    queryKey: ["artist-story-replies", artistId, cursor],
    queryFn: () => storyReplies(artistId, cursor),
    enabled: open,
    retry: false,
  });
  const items: StoryReply[] = list.data?.items ?? [];
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>限動回覆</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          粉絲對限時動態的私下回覆，只有藝人與工作人員看得到，不會公開；保留 30 天。
        </p>
        {!open && (
          <Button variant="outline" onClick={() => setOpen(true)}>
            查看限動回覆
          </Button>
        )}
        {open && (
          <>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={list.isFetching} onClick={() => void list.refetch()}>
                重新整理
              </Button>
              {pages.length > 1 && (
                <Button variant="outline" size="sm" disabled={list.isFetching} onClick={() => setPages([null])}>
                  回到最新
                </Button>
              )}
            </div>
            {list.isPending && <p>讀取回覆…</p>}
            {list.isError && (
              <p role="alert" className="text-destructive">
                {list.error instanceof Error ? list.error.message : "讀取失敗，請重試"}
              </p>
            )}
            {list.data && items.length === 0 && <p className="text-sm text-muted-foreground">目前沒有限動回覆。</p>}
            {items.length > 0 && (
              <ul className="divide-y">
                {items.map((r) => (
                  <li key={r.replyId} className="flex gap-3 py-2">
                    {r.fromAvatarUrl ? (
                      <img src={r.fromAvatarUrl} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                    ) : (
                      <span className="h-8 w-8 shrink-0 rounded-full bg-muted" aria-hidden />
                    )}
                    <div className="min-w-0">
                      <p className="text-sm">
                        <strong>{r.fromName}</strong>
                        <span className="ml-2 text-xs text-muted-foreground">{fmt(r.createdAt)} · 限動 {r.contentId.slice(0, 8)}</span>
                      </p>
                      <p className="break-words">{r.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {list.data?.nextCursor && (
              <Button variant="outline" size="sm" disabled={list.isFetching} onClick={() => setPages((p) => [...p, list.data?.nextCursor ?? null])}>
                較舊的回覆
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
